/**
 * Local font installation. Font files the user drops in are parsed for their
 * real family/weight/style (sfnt name table), registered with the document
 * via the FontFace API, and persisted in IndexedDB so they survive reloads.
 * Nothing ever leaves the browser; fonts are stored only on this machine.
 */

export interface InstalledFontMeta {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
}

const NAME_TABLE_TAG = 0x6e616d65;
const CMAP_TABLE_TAG = 0x636d6170;
const SFNT_VERSIONS = new Set([0x00010000, 0x4f54544f, 0x74727565]);

function sfntTable(view: DataView, tag: number): { offset: number; length: number } | null {
  if (view.byteLength < 12 || !SFNT_VERSIONS.has(view.getUint32(0))) return null;
  const numTables = view.getUint16(4);
  for (let index = 0; index < numTables; index += 1) {
    const base = 12 + index * 16;
    if (base + 16 > view.byteLength) return null;
    if (view.getUint32(base) !== tag) continue;
    const offset = view.getUint32(base + 8);
    const length = view.getUint32(base + 12);
    return offset + length <= view.byteLength ? { offset, length } : null;
  }
  return null;
}

function weightFromSubfamily(subfamily: string): number {
  // Collapse spaces/hyphens so "Extra Bold" and "Ultra-Light" match too.
  const value = subfamily.toLowerCase().replace(/[\s_-]+/g, '');
  if (/black|heavy/.test(value)) return 900;
  if (/extrabold|ultrabold/.test(value)) return 800;
  if (/semibold|demibold/.test(value)) return 600;
  if (/bold/.test(value)) return 700;
  if (/medium/.test(value)) return 500;
  if (/extralight|ultralight/.test(value)) return 200;
  if (/thin/.test(value)) return 100;
  if (/light/.test(value)) return 300;
  return 400;
}

// WOFF/WOFF2 wrap an sfnt but compress the tables, so parseFontMeta can't read
// their name table without a decompressor. Derive identity from the filename
// instead of silently defaulting every one to weight 400 / normal.
function metaFromFilename(name: string): InstalledFontMeta {
  const base = name.replace(/\.(ttf|otf|woff2?)$/i, '');
  const family = base
    .replace(/[-_ ]+(thin|x?thin|extralight|ultralight|light|regular|book|roman|medium|semibold|demibold|bold|extrabold|ultrabold|black|heavy)?[-_ ]*(italic|oblique)?$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return {
    family: family || base,
    weight: weightFromSubfamily(base),
    style: /italic|oblique/i.test(base) ? 'italic' : 'normal',
  };
}

/** Reads family/subfamily from an sfnt (ttf/otf) name table; null when unparseable. */
export function parseFontMeta(buffer: ArrayBuffer): InstalledFontMeta | null {
  if (buffer.byteLength < 12) return null;
  const view = new DataView(buffer);
  if (!SFNT_VERSIONS.has(view.getUint32(0))) return null;
  const numTables = view.getUint16(4);
  let nameOffset = 0;
  for (let index = 0; index < numTables; index += 1) {
    const base = 12 + index * 16;
    if (base + 16 > buffer.byteLength) return null;
    if (view.getUint32(base) === NAME_TABLE_TAG) {
      nameOffset = view.getUint32(base + 8);
      break;
    }
  }
  if (!nameOffset || nameOffset + 6 > buffer.byteLength) return null;
  const count = view.getUint16(nameOffset + 2);
  const stringOffset = nameOffset + view.getUint16(nameOffset + 4);
  let family: string | undefined;
  let subfamily: string | undefined;
  let preferredFamily: string | undefined;
  let preferredSubfamily: string | undefined;
  for (let index = 0; index < count; index += 1) {
    const record = nameOffset + 6 + index * 12;
    if (record + 12 > buffer.byteLength) break;
    const platform = view.getUint16(record);
    const nameId = view.getUint16(record + 6);
    if (nameId !== 1 && nameId !== 2 && nameId !== 16 && nameId !== 17) continue;
    const length = view.getUint16(record + 8);
    const start = stringOffset + view.getUint16(record + 10);
    if (start + length > buffer.byteLength) continue;
    let value = '';
    if (platform === 3 || platform === 0) {
      for (let cursor = 0; cursor + 1 < length; cursor += 2) value += String.fromCharCode(view.getUint16(start + cursor));
    } else {
      for (let cursor = 0; cursor < length; cursor += 1) value += String.fromCharCode(view.getUint8(start + cursor));
    }
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (nameId === 1) family ??= trimmed;
    else if (nameId === 2) subfamily ??= trimmed;
    else if (nameId === 16) preferredFamily ??= trimmed;
    else preferredSubfamily ??= trimmed;
  }
  const resolvedFamily = preferredFamily ?? family;
  if (!resolvedFamily) return null;
  const resolvedSubfamily = preferredSubfamily ?? subfamily ?? '';
  return {
    family: resolvedFamily,
    weight: weightFromSubfamily(resolvedSubfamily),
    style: /italic|oblique/i.test(resolvedSubfamily) ? 'italic' : 'normal',
  };
}

/** Reads Unicode coverage from sfnt cmap format 4/12 tables. */
export function parseFontGlyphCodePoints(buffer: ArrayBuffer, maximum = 65_536): number[] {
  const view = new DataView(buffer);
  const table = sfntTable(view, CMAP_TABLE_TAG);
  if (!table || table.length < 4) return [];
  const count = view.getUint16(table.offset + 2);
  const candidates: Array<{ format: number; offset: number }> = [];
  for (let index = 0; index < count; index += 1) {
    const record = table.offset + 4 + index * 8;
    if (record + 8 > table.offset + table.length) break;
    const offset = table.offset + view.getUint32(record + 4);
    if (offset + 2 > table.offset + table.length) continue;
    const format = view.getUint16(offset);
    if (format === 12 || format === 4) candidates.push({ format, offset });
  }
  candidates.sort((left, right) => right.format - left.format);
  const result = new Set<number>();
  for (const candidate of candidates) {
    if (candidate.format === 12) {
      if (candidate.offset + 16 > view.byteLength) continue;
      const length = view.getUint32(candidate.offset + 4);
      const groups = view.getUint32(candidate.offset + 12);
      if (candidate.offset + length > view.byteLength || groups > 100_000) continue;
      for (let index = 0; index < groups && result.size < maximum; index += 1) {
        const group = candidate.offset + 16 + index * 12;
        if (group + 12 > candidate.offset + length) break;
        const start = view.getUint32(group);
        const end = Math.min(0x10ffff, view.getUint32(group + 4));
        for (let codePoint = start; codePoint <= end && result.size < maximum; codePoint += 1) {
          if (codePoint < 0xd800 || codePoint > 0xdfff) result.add(codePoint);
        }
      }
    } else {
      if (candidate.offset + 16 > view.byteLength) continue;
      const length = view.getUint16(candidate.offset + 2);
      const segCount = view.getUint16(candidate.offset + 6) / 2;
      if (candidate.offset + length > view.byteLength || !Number.isInteger(segCount) || segCount > 32_768) continue;
      const endCodes = candidate.offset + 14;
      const startCodes = endCodes + segCount * 2 + 2;
      const deltas = startCodes + segCount * 2;
      const rangeOffsets = deltas + segCount * 2;
      for (let segment = 0; segment < segCount && result.size < maximum; segment += 1) {
        const start = view.getUint16(startCodes + segment * 2);
        const end = view.getUint16(endCodes + segment * 2);
        const delta = view.getInt16(deltas + segment * 2);
        const rangeOffsetAddress = rangeOffsets + segment * 2;
        const rangeOffset = view.getUint16(rangeOffsetAddress);
        for (let codePoint = start; codePoint <= end && codePoint !== 0xffff && result.size < maximum; codePoint += 1) {
          let glyph = (codePoint + delta) & 0xffff;
          if (rangeOffset) {
            const address = rangeOffsetAddress + rangeOffset + (codePoint - start) * 2;
            if (address + 2 > candidate.offset + length) continue;
            glyph = view.getUint16(address);
            if (glyph) glyph = (glyph + delta) & 0xffff;
          }
          if (glyph) result.add(codePoint);
        }
      }
    }
    if (result.size) break;
  }
  return [...result].sort((left, right) => left - right);
}

interface StoredFont extends InstalledFontMeta {
  bytes: ArrayBuffer;
  fileName: string;
  addedAt: string;
}

const DB_NAME = 'maipo-fonts';
const STORE = 'fonts';

function openFontDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Font storage is unavailable.'));
  });
}

function storeRequest<T>(database: IDBDatabase, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE, mode);
    transaction.onabort = () => reject(transaction.error ?? new Error('Font storage transaction aborted.'));
    try {
      const request = run(transaction.objectStore(STORE));
      // Request success only means the write was queued, not committed.
      transaction.oncomplete = () => resolve(request.result);
    } catch (error) {
      try { transaction.abort(); } catch { /* The transaction may already be finished. */ }
      reject(error instanceof Error ? error : new Error('Font storage request failed.'));
    }
  });
}

async function prepareFace(meta: InstalledFontMeta, bytes: ArrayBuffer): Promise<FontFace> {
  const face = new FontFace(meta.family, bytes, { weight: String(meta.weight), style: meta.style });
  await face.load();
  return face;
}

function activateFace(face: FontFace): void {
  // Add the validated face first: a rejected replacement must leave the prior
  // working face available. Remove duplicate identities only after activation.
  document.fonts.add(face);
  for (const existing of [...document.fonts]) {
    if (existing !== face && existing.family === face.family && existing.weight === face.weight && existing.style === face.style) {
      document.fonts.delete(existing);
    }
  }
}

export interface InstallFontsResult {
  installed: InstalledFontMeta[];
  failed: string[];
}

/** Parses, registers, and persists dropped font files. */
export async function installFontFiles(files: readonly File[]): Promise<InstallFontsResult> {
  const installed: InstalledFontMeta[] = [];
  const failed: string[] = [];
  let database: IDBDatabase;
  try { database = await openFontDb(); } catch {
    return { installed, failed: files.map(file => file.name) };
  }
  try {
    for (const file of files) {
      try {
        const bytes = await file.arrayBuffer();
        const meta = parseFontMeta(bytes) ?? metaFromFilename(file.name);
        const face = await prepareFace(meta, bytes);
        const stored: StoredFont = { ...meta, bytes, fileName: file.name, addedAt: new Date().toISOString() };
        await storeRequest(database, 'readwrite', (store) => store.put(stored, `${meta.family}|${String(meta.weight)}|${meta.style}`));
        activateFace(face);
        installed.push(meta);
      } catch {
        failed.push(file.name);
      }
    }
  } finally {
    database.close();
  }
  return { installed, failed };
}

/** Re-registers all persisted fonts; returns how many were restored. */
export async function loadStoredFonts(): Promise<number> {
  let database: IDBDatabase;
  try {
    database = await openFontDb();
  } catch {
    return 0;
  }
  try {
    const records = await storeRequest<StoredFont[]>(database, 'readonly', (store) => store.getAll() as IDBRequest<StoredFont[]>);
    let restored = 0;
    for (const record of records) {
      try {
        activateFace(await prepareFace(record, record.bytes));
        restored += 1;
      } catch {
        // a single corrupt record must not block the rest
      }
    }
    return restored;
  } catch {
    return 0;
  } finally {
    database.close();
  }
}

/** Returns the installed face's cmap coverage; system fonts do not expose bytes. */
export async function installedFontCodePoints(family: string, weight: number): Promise<number[]> {
  let database: IDBDatabase;
  try {
    database = await openFontDb();
  } catch {
    return [];
  }
  try {
    const records = await storeRequest<StoredFont[]>(database, 'readonly', (store) => store.getAll() as IDBRequest<StoredFont[]>);
    const familyMatches = records.filter((record) => record.family.toLowerCase() === family.toLowerCase());
    const match = familyMatches.sort((left, right) => Math.abs(left.weight - weight) - Math.abs(right.weight - weight))[0];
    return match ? parseFontGlyphCodePoints(match.bytes) : [];
  } catch {
    return [];
  } finally {
    database.close();
  }
}
